// src/components/CottageList.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { translateApiMessage } from "../utils/formErrors";
import { useSelector, useDispatch } from "react-redux";
import {
  fetchCottages,
  deleteCottages,
} from "../actions/cottageActions";
import { fetchOrders } from "../actions/performaActions";
import { Link, useNavigate } from "react-router-dom";
import { formatNumber } from "../utils/numberFormat";
import PaginationControls from "./PaginationControls";
import Select from "./ui/Select";
import DatePicker from "react-multi-date-picker";
import persian from "react-date-object/calendars/persian";
import persian_fa from "react-date-object/locales/persian_fa";
import moment from "moment-jalaali";
import { fetchCostumers } from "../actions/authActions";
import axiosInstance from "../utils/axiosInstance";
import HotToastMessage from "./ui/HotToastMessage";
import ConfirmModal from "./ui/ConfirmModal";
import {
  MoveDown,
  MoveUp,
  Filter,
  Loader2,
  Trash2,
  FileSpreadsheet,
  Search,
  X,
} from "lucide-react";

// Reusable slide-over
import FilterSlideOver, { AccordionItem } from "./ui/FilterSlideOver";

// NEW: overlay summary
import CottageSummaryOverlay from "./CottageSummaryOverlay";

moment.loadPersian({ dialect: "persian-modern" });

const CottageList = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // ---- Auth guard
  const auth = useSelector((state) => state.auth);
  useEffect(() => {
    if (auth.isAuthenticated === false) navigate("/");
  }, [auth.isAuthenticated, navigate]);

  // ---- External datasets
  const orders = useSelector((state) => state.order.orders);
  const orderOptions = useMemo(
    () =>
      [
        { value: "__patteh__", label: "پته" },
        ...(orders || []).map((order) => ({
          value: order.prf_order_no,
          label: order.prf_order_no,
        })),
      ],
    [orders]
  );

  const costumerstate = useSelector((state) => state.costumers);
  const { costumerList } = costumerstate || {
    costumerList: [],
    costumersLoading: false,
    costumersError: null,
  };

  // ---- Local UI state
  const [rafeeTaahod, setRafeeTaahod] = useState("");
  const [docsRecieved, setDocsRecieved] = useState("");
  const [rewatchStatus, setRewatchStatus] = useState("");
  const [exitedStatus, setExitedStatus] = useState("");

  const [searchText, setSearchText] = useState("");
  const [query, setQuery] = useState("");
  const [, setAutomationLoading] = useState(false);

  const [sortConfig, setSortConfig] = useState({ ordering: "" });

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  const [cottageDate, setCottageDate] = useState("");
  const [cottageDateBefore, setCottageDateBefore] = useState("");
  const [prfOrderNo, setPrfOrderNo] = useState("");

  const [paymentDateAfter, setPaymentDateAfter] = useState("");
  const [paymentDateBefore, setPaymentDateBefore] = useState("");

  const [referencePresent, setReferencePresent] = useState("");

  const [selectedCottages, setSelectedCottages] = useState([]);
  const [areAllSelected, setAreAllSelected] = useState(false);

  const [toast, setToast] = useState({ message: "", type: "info" });
  const [showModal, setShowModal] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);

  // NEW: overlay state
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [activeCottage, setActiveCottage] = useState(null);

  // ---- Cottages slice
  const {
    cottages,
    loading,
    error,
    next,
    previous,
    count,
    updatingCurrencyPrice,
    updateCurrencyPriceError,
  } = useSelector((state) => state.cottages);

  const totalPages = count ? Math.ceil(count / pageSize) : 1;

  // ---- Active filters count
  const activeFiltersCount = useMemo(
    () =>
      [
        prfOrderNo,
        cottageDate,
        cottageDateBefore,
        rafeeTaahod,
        docsRecieved,
        rewatchStatus,
        exitedStatus,
        paymentDateAfter,
        paymentDateBefore,
        referencePresent,
        query,
      ].filter(Boolean).length,
    [
      prfOrderNo,
      cottageDate,
      cottageDateBefore,
      rafeeTaahod,
      docsRecieved,
      rewatchStatus,
      exitedStatus,
      paymentDateAfter,
      paymentDateBefore,
      referencePresent,
      query,
    ]
  );

  // ---- Utils
  const convertToWesternDigits = (str) => {
    if (typeof str !== "string") return str;
    const easternDigits = ["۰", "۱", "۲", "۳", "۴", "۵", "۶", "۷", "۸", "۹"];
    const westernDigits = ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9"];
    return str.replace(
      /[۰-۹]/g,
      (d) => westernDigits[easternDigits.indexOf(d)]
    );
  };

  const handleCottageDateChange = (date) => {
    if (date && date.format) setCottageDate(date.format("YYYY-MM-DD"));
    else setCottageDate("");
  };
  const handleCottageDateBeforeChange = (date) => {
    if (date && date.format) setCottageDateBefore(date.format("YYYY-MM-DD"));
    else setCottageDateBefore("");
  };
  const handlePaymentDateAfterChange = (date) => {
    if (date && date.format) setPaymentDateAfter(date.format("YYYY-MM-DD"));
    else setPaymentDateAfter("");
  };
  const handlePaymentDateBeforeChange = (date) => {
    if (date && date.format) setPaymentDateBefore(date.format("YYYY-MM-DD"));
    else setPaymentDateBefore("");
  };

  // NEW: open summary overlay
  const openSummary = (cottage) => {
    setActiveCottage(cottage);
    setSummaryOpen(true);
  };
  const closeSummary = () => {
    setSummaryOpen(false);
    setActiveCottage(null);
  };

  // ---- Debounced search
  useEffect(() => {
    const handler = setTimeout(() => {
      setQuery(searchText.trim());
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(handler);
  }, [searchText]);

  // Build the same filters that fetchCottages uses (single source of truth)
  const buildCottageFilters = useCallback(() => {
    const filters = {
      search: query,
      cottageDate: convertToWesternDigits(cottageDate),
      cottageDateBefore: convertToWesternDigits(cottageDateBefore),
      prfOrderNo,
      ...(rafeeTaahod && { rafee_taahod: rafeeTaahod }),
      ...(docsRecieved && { docs_recieved: docsRecieved }),
      ...(rewatchStatus && { rewatch: rewatchStatus }),
      ...(exitedStatus && { exited: exitedStatus }),
      ...(paymentDateAfter && {
        cottage_payment_date_after: convertToWesternDigits(paymentDateAfter),
      }),
      ...(paymentDateBefore && {
        cottage_payment_date_before: convertToWesternDigits(paymentDateBefore),
      }),
      ...(referencePresent && { reference_present: referencePresent }),
      ...(sortConfig.ordering && { ordering: sortConfig.ordering }),
    };

    return filters;
  }, [query, cottageDate, cottageDateBefore, prfOrderNo, rafeeTaahod, docsRecieved, rewatchStatus, exitedStatus, paymentDateAfter, paymentDateBefore, referencePresent, sortConfig.ordering]);

  // ---- Data fetch
  useEffect(() => {
    dispatch(fetchCostumers());
    dispatch(fetchOrders());

    const filters = buildCottageFilters();

    dispatch(fetchCottages(currentPage, pageSize, filters));
  }, [
    dispatch,
    currentPage,
    pageSize,
    query,
    cottageDate,
    cottageDateBefore,
    prfOrderNo,
    rafeeTaahod,
    docsRecieved,
    rewatchStatus,
    exitedStatus,
    sortConfig.ordering,
    paymentDateAfter,
    paymentDateBefore,
    referencePresent,
    buildCottageFilters,
  ]);

  // ---- Pagination handlers
  const hasNext = !!next;
  const hasPrevious = !!previous;
  const handlePageChange = (e) => setCurrentPage(Number(e.target.value));
  const handleNextPage = () => hasNext && setCurrentPage((p) => p + 1);
  const handlePreviousPage = () => hasPrevious && setCurrentPage((p) => p - 1);
  const handlePageSizeChange = (e) => {
    setPageSize(Number(e.target.value));
    setCurrentPage(1);
  };

  // ---- Selection
  const handleSelectCottage = (event, cottage) => {
    const { checked } = event.target;
    const updated = checked
      ? [...selectedCottages, cottage]
      : selectedCottages.filter((c) => c.id !== cottage.id);
    setSelectedCottages(updated);
    setAreAllSelected(updated.length === cottages.length);
  };

  const handleSelectAll = (event) => {
    const { checked } = event.target;
    setAreAllSelected(checked);
    setSelectedCottages(checked ? cottages : []);
  };

  // ---- Sort
  const handleSort = (field) => {
    setSortConfig((prev) => {
      const isDescending = prev.ordering === field;
      return { ordering: isDescending ? `-${field}` : field };
    });
    setCurrentPage(1);
  };

  // ---- Bulk actions
  const handleExport = async () => {
    if (!selectedCottages.length) {
      setToast({
        message: "لطفاً حداقل یک کوتاژ را انتخاب کنید.",
        type: "warn",
      });
      return;
    }
    const cottageNumbers = selectedCottages.map((c) => c.cottage_number);
    try {
      const res = await axiosInstance.post(
        "cottages/export-excel/",
        { cottage_numbers: cottageNumbers },
        { responseType: "blob" }
      );
      const blob = new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const filename = `cottages_${new Date().toISOString().slice(0, 10)}.xlsx`;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setToast({ message: "فایل اکسل با موفقیت صادر شد.", type: "success" });
    } catch (err) {
      console.error("Export error:", err);
      setToast({ message: "خطا در صدور فایل اکسل.", type: "error" });
    }
  };

  // NEW: Export ALL cottages that match current filters (ignores pagination)
  const handleExportFiltered = async () => {
    try {
      const filters = buildCottageFilters();

      // Turn filters into query params exactly like your list endpoint expects
      const params = new URLSearchParams();

      if (filters.prfOrderNo === "__patteh__") {
        params.append("is_patteh", "true");
      } else if (filters.prfOrderNo) {
        params.append("proforma", filters.prfOrderNo);
      }

      if (filters.cottageDate)
        params.append("cottage_date_after", filters.cottageDate);
      if (filters.cottageDateBefore)
        params.append("cottage_date_before", filters.cottageDateBefore);

      if (filters.docs_recieved)
        params.append("docs_recieved", filters.docs_recieved);
      if (filters.rafee_taahod)
        params.append("rafee_taahod", filters.rafee_taahod);
      if (filters.rewatch) params.append("rewatch", filters.rewatch);
      if (filters.exited) params.append("exited", filters.exited);

      if (filters.cottage_payment_date_after)
        params.append(
          "cottage_payment_date_after",
          filters.cottage_payment_date_after
        );

      if (filters.cottage_payment_date_before)
        params.append(
          "cottage_payment_date_before",
          filters.cottage_payment_date_before
        );

      if (filters.reference_present)
        params.append("reference_present", filters.reference_present);

      if (filters.search) params.append("search", filters.search);
      if (filters.ordering) params.append("ordering", filters.ordering);

      const res = await axiosInstance.get(
        `/export-cottages-excel-filtered/?${params.toString()}`,
        { responseType: "blob" }
      );

      const blob = new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;

      const filename = `cottages_filtered_${new Date()
        .toISOString()
        .slice(0, 10)}.xlsx`;
      link.setAttribute("download", filename);

      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setToast({
        message: "اکسلِ فیلتر شده با موفقیت صادر شد.",
        type: "success",
      });
    } catch (err) {
      console.error("Export filtered error:", err);
      setToast({ message: "خطا در صدور اکسلِ فیلتر شده.", type: "error" });
    }
  };

  const handleStartAutomation = async () => {
    if (!selectedCottages.length) {
      setToast({
        message: "لطفاً حداقل یک کوتاژ را انتخاب کنید.",
        type: "warn",
      });
      return;
    }
    const cottageNumbers = selectedCottages.map((c) => c.cottage_number);
    try {
      setAutomationLoading(true);
      const res = await axiosInstance.post("/start-epl-automation/", {
        cottage_numbers: cottageNumbers,
      });
      setToast({
        message: res.data.message || "اتوماسیون آغاز شد.",
        type: "success",
      });
    } catch (err) {
      console.error("Automation error:", err);
      setToast({
        message:
          translateApiMessage(err.response?.data?.error) || "خطا در راه‌اندازی اتوماسیون EPL.",
        type: "error",
      });
    } finally {
      setAutomationLoading(false);
    }
  };
  // Retained for the currently disabled automation controls below.
  void handleStartAutomation;

  // ---- Delete flow
  const handleConfirmDelete = () => {
    setShowModal(false);
    const ids = selectedCottages.map((c) => c.id);
    dispatch(deleteCottages(ids))
      .then(() => {
        setToast({
          message: "کوتاژهای انتخاب‌شده با موفقیت حذف شدند.",
          type: "success",
        });
        setSelectedCottages([]);
        dispatch(fetchCottages(currentPage));
      })
      .catch((error) => {
        console.error("خطا در حذف کوتاژها:", error);
        setToast({ message: "خطا در حذف کوتاژها.", type: "error" });
      });
  };
  const handleCancelDelete = () => setShowModal(false);

  // ---- Clear all filters
  const clearFilters = () => {
    setPrfOrderNo("");
    setCottageDate("");
    setCottageDateBefore("");
    setRafeeTaahod("");
    setDocsRecieved("");
    setRewatchStatus("");
    setExitedStatus("");
    setPaymentDateAfter("");
    setPaymentDateBefore("");
    setReferencePresent("");
    setSearchText("");
  };

  // ---- React-Select styles
  const selectStyles = {
    control: (base) => ({
      ...base,
      background: "rgba(255,255,255,0.98)",
      borderColor: "#E5E7EB",
      borderRadius: "1rem",
      padding: "0.2rem",
      boxShadow: "none",
      textAlign: "right",
      minHeight: "44px",
      ":hover": { borderColor: "#2563EB" },
    }),
    menu: (base) => ({ ...base, textAlign: "right", zIndex: 60 }),
    option: (base, state) => ({
      ...base,
      textAlign: "right",
      backgroundColor: state.isSelected
        ? "#2563EB"
        : state.isFocused
        ? "#DBEAFE"
        : "#fff",
      color: state.isSelected ? "#fff" : "#111827",
    }),
    singleValue: (base) => ({ ...base, textAlign: "right" }),
    placeholder: (base) => ({ ...base, textAlign: "right" }),
  };

  // ---- Header chips
  const filterChips = useMemo(() => {
    const chips = [];
    if (query) chips.push({ k: "جستجو", v: query });
    if (prfOrderNo) {
      chips.push(prfOrderNo === "__patteh__"
        ? { k: "نوع اظهارنامه", v: "پته" }
        : { k: "ثبت سفارش", v: prfOrderNo });
    }
    if (cottageDate) chips.push({ k: "از تاریخ", v: cottageDate });
    if (cottageDateBefore) chips.push({ k: "تا تاریخ", v: cottageDateBefore });
    if (paymentDateAfter) chips.push({ k: "پرداخت از", v: paymentDateAfter });
    if (paymentDateBefore) chips.push({ k: "پرداخت تا", v: paymentDateBefore });
    if (rafeeTaahod) chips.push({ k: "رفع تعهد", v: rafeeTaahod });
    if (docsRecieved) chips.push({ k: "اخذ مدارک", v: docsRecieved });
    if (rewatchStatus) chips.push({ k: "بازبینی", v: rewatchStatus });
    if (exitedStatus) chips.push({ k: "خروج شده", v: exitedStatus });
    if (referencePresent) chips.push({ k: "مرجع", v: referencePresent });
    return chips;
  }, [
    query,
    prfOrderNo,
    cottageDate,
    cottageDateBefore,
    paymentDateAfter,
    paymentDateBefore,
    rafeeTaahod,
    docsRecieved,
    rewatchStatus,
    exitedStatus,
    referencePresent,
  ]);

  const activeCustomer = useMemo(() => {
    if (!activeCottage) return null;
    return (
      costumerList?.find(
        (cust) => Number(cust.id) === Number(activeCottage.cottage_customer)
      ) || null
    );
  }, [activeCottage, costumerList]);

  return (
    <div className="mt-24 pb-24 bg-slate-50 min-h-screen font-vazir" dir="rtl">
      {/* Toast & Confirm */}
      <HotToastMessage
        message={toast.message}
        type={toast.type}
        duration={3000}
        onClose={() => setToast({ message: "", type: "info" })}
        position="top"
      />
      <ConfirmModal
        isOpen={showModal}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />

      {/* NEW: Summary Overlay */}
      <CottageSummaryOverlay
        isOpen={summaryOpen}
        onClose={closeSummary}
        cottage={activeCottage}
        customer={activeCustomer}
      />

      {/* Main container */}
      <div className="mx-auto max-w-7xl md:max-w-[90%] px-4 sm:px-6 lg:px-8">
        <div className="rounded-3xl bg-white p-5 sm:p-6 shadow-sm ring-1 ring-slate-200">
          {/* Header + toolbar */}
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div className="text-right">
              <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900">
                لیست اظهارنامه‌ها
              </h2>
              <div className="mt-2 flex flex-wrap items-center justify-end gap-2 text-xs">
                <span className="rounded-full bg-slate-100 px-2.5 py-1 text-slate-700">
                  تعداد: {count ?? 0}
                </span>
                {activeFiltersCount > 0 ? (
                  <span className="rounded-full bg-blue-50 px-2.5 py-1 text-blue-700">
                    {activeFiltersCount} فیلتر فعال
                  </span>
                ) : (
                  <span className="rounded-full bg-slate-100 px-2.5 py-1 text-slate-700">
                    بدون فیلتر
                  </span>
                )}
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => setFiltersOpen(true)}
                className="inline-flex items-center gap-2 rounded-2xl bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 transition"
              >
                <Filter className="w-4 h-4" />
                فیلترها
              </button>

              {activeFiltersCount > 0 && (
                <button
                  onClick={clearFilters}
                  className="inline-flex items-center gap-2 rounded-2xl bg-white px-4 py-2 text-sm ring-1 ring-slate-200 hover:bg-slate-50 transition"
                >
                  <X className="w-4 h-4" />
                  پاک‌سازی
                </button>
              )}
            </div>
          </div>

          {/* Search */}
          <div className="mt-5">
            <label className="block text-sm font-medium text-slate-700 text-right mb-2">
              جستجو (کد/نام فارسی/نام انگلیسی):
            </label>
            <div className="relative">
              <input
                type="text"
                placeholder="جستجوی خود را وارد کنید…"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 pr-11 text-sm text-slate-800 outline-none transition focus:ring-4 focus:ring-blue-100"
              />
              <Search className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            </div>

            {filterChips.length > 0 && (
              <div className="mt-3 flex flex-wrap justify-end gap-2">
                {filterChips.map((c, idx) => (
                  <span
                    key={idx}
                    className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 text-blue-700 text-xs border border-blue-100"
                  >
                    <b className="font-medium">{c.k}:</b> {c.v}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Action buttons */}
          <div className="mt-5 flex flex-wrap gap-2">
            <button
              onClick={() => setShowModal(true)}
              disabled={!selectedCottages.length}
              className="inline-flex items-center gap-2 rounded-2xl bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 transition disabled:bg-slate-200 disabled:text-slate-500 disabled:cursor-not-allowed"
            >
              <Trash2 className="w-4 h-4" />
              حذف انتخابی‌ها
            </button>

            <button
              onClick={handleExport}
              disabled={!selectedCottages.length}
              className="inline-flex items-center gap-2 rounded-2xl bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700 transition disabled:bg-slate-200 disabled:text-slate-500 disabled:cursor-not-allowed"
            >
              <FileSpreadsheet className="w-4 h-4" />
              خروجی اکسل
            </button>

            {/* NEW: Export current filter (all records) */}
            <button
              onClick={handleExportFiltered}
              className="inline-flex items-center gap-2 rounded-2xl bg-emerald-50 px-4 py-2 text-sm font-medium text-emerald-700 hover:bg-emerald-100 transition ring-1 ring-emerald-200"
            >
              <FileSpreadsheet className="w-4 h-4" />
              اکسل کل فیلتر شده
            </button>

            {/* <button
              onClick={handleStartAutomation}
              disabled={!selectedCottages.length || automationLoading}
              className="inline-flex items-center gap-2 rounded-2xl bg-sky-600 px-4 py-2 text-sm font-medium text-white hover:bg-sky-700 transition disabled:bg-slate-200 disabled:text-slate-500 disabled:cursor-not-allowed"
            >
              {automationLoading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  در حال اجرا…
                </>
              ) : (
                <>
                  <Play className="w-4 h-4" />
                  شروع اتوماسیون EPL
                </>
              )}
            </button> */}
          </div>

          {/* Loading / Error */}
          {loading && (
            <div className="mt-8 grid gap-3">
              <div className="h-10 rounded-2xl bg-slate-100 animate-pulse" />
              <div className="h-10 rounded-2xl bg-slate-100 animate-pulse" />
              <div className="h-10 rounded-2xl bg-slate-100 animate-pulse" />
            </div>
          )}

          {error && !loading && (
            <div className="mt-6 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-red-700">
              {error}
            </div>
          )}

          {/* Table & Pagination */}
          {!loading && !error && (
            <>
              {cottages.length === 0 ? (
                <div className="mt-10 text-center text-slate-600">
                  موردی یافت نشد.
                </div>
              ) : (
                <>
                  <div className="mt-5">
                    <PaginationControls
                      currentPage={currentPage}
                      totalPages={totalPages}
                      pageSize={pageSize}
                      pageSizeOptions={[10, 20, 50, 100, 200]}
                      hasNext={hasNext}
                      hasPrevious={hasPrevious}
                      onPageChange={handlePageChange}
                      onNextPage={handleNextPage}
                      onPreviousPage={handlePreviousPage}
                      onPageSizeChange={handlePageSizeChange}
                    />
                  </div>

                  <div className="mt-3 overflow-hidden rounded-3xl bg-white ring-1 ring-slate-200">
                    <div className="overflow-x-auto">
                      <table className="min-w-full text-sm">
                        <thead className="bg-slate-50 sticky top-0">
                          <tr className="text-right text-slate-700">
                            <th className="p-3">
                              <input
                                type="checkbox"
                                onChange={handleSelectAll}
                                checked={areAllSelected && cottages.length > 0}
                              />
                            </th>
                            <th className="p-3 font-semibold">ردیف</th>
                            <th className="p-3 font-semibold">شماره کوتاژ</th>
                            <th
                              onClick={() => handleSort("cottage_date")}
                              className="p-3 font-semibold cursor-pointer select-none hover:bg-slate-100 transition"
                              title="مرتب‌سازی بر اساس تاریخ"
                            >
                              <span className="inline-flex items-center gap-1">
                                تاریخ
                                {sortConfig.ordering === "cottage_date" && (
                                  <MoveUp className="w-4 h-4 text-blue-600" />
                                )}
                                {sortConfig.ordering === "-cottage_date" && (
                                  <MoveDown className="w-4 h-4 text-blue-600" />
                                )}
                              </span>
                            </th>
                            <th className="p-3 font-semibold">شماره پرفورم</th>
                            <th className="p-3 font-semibold">
                              شماره ثبت سفارش
                            </th>
                            <th className="p-3 font-semibold">نام مشتری</th>

                            <th
                              onClick={() => handleSort("total_value")}
                              className="p-3 font-semibold cursor-pointer select-none hover:bg-slate-100 transition"
                            >
                              <span className="inline-flex items-center gap-1">
                                ارزش کل
                                {sortConfig.ordering === "total_value" && (
                                  <MoveUp className="w-4 h-4 text-blue-600" />
                                )}
                                {sortConfig.ordering === "-total_value" && (
                                  <MoveDown className="w-4 h-4 text-blue-600" />
                                )}
                              </span>
                            </th>

                            <th
                              onClick={() => handleSort("customs_value")}
                              className="p-3 font-semibold cursor-pointer select-none hover:bg-slate-100 transition"
                            >
                              <span className="inline-flex items-center gap-1">
                                ارزش گمرکی
                                {sortConfig.ordering === "customs_value" && (
                                  <MoveUp className="w-4 h-4 text-blue-600" />
                                )}
                                {sortConfig.ordering === "-customs_value" && (
                                  <MoveDown className="w-4 h-4 text-blue-600" />
                                )}
                              </span>
                            </th>

                            <th className="p-3 font-semibold">هزینه‌ها</th>
                            <th className="p-3 font-semibold">نرخ ارز</th>
                            <th className="p-3 font-semibold">ارزش افزوده</th>
                            <th className="p-3 font-semibold">رفع تعهد</th>
                            <th className="p-3 font-semibold">اخذ مدارک</th>
                            <th className="p-3 font-semibold">بازبینی</th>
                            <th className="p-3 font-semibold">خروج شده</th>
                            <th className="p-3"></th>
                          </tr>
                        </thead>

                        <tbody className="divide-y divide-slate-100">
                          {cottages.map((cottage, index) => {
                            const customer = costumerList.find(
                              (cust) => cust.id === cottage.cottage_customer
                            );

                            const isValueMismatch =
                              cottage?.customs_value != null &&
                              cottage?.customs_value_epl != null &&
                              Number(cottage.customs_value) !==
                                Number(cottage.customs_value_epl);

                            return (
                              <tr
                                key={cottage.id}
                                className={[
                                  "hover:bg-slate-50 transition",
                                  isValueMismatch ? "bg-sky-50" : "",
                                ].join(" ")}
                              >
                                <td className="p-3 align-middle">
                                  <input
                                    type="checkbox"
                                    checked={selectedCottages.some(
                                      (s) => s.id === cottage.id
                                    )}
                                    onChange={(e) =>
                                      handleSelectCottage(e, cottage)
                                    }
                                  />
                                </td>

                                <td className="p-3 align-middle">
                                  {(currentPage - 1) * pageSize + (index + 1)}
                                </td>

                                <td className="p-3 align-middle font-medium">
                                  <button
                                    type="button"
                                    onClick={() => openSummary(cottage)}
                                    className="text-blue-700 hover:underline"
                                  >
                                    {cottage.cottage_number}
                                  </button>
                                </td>

                                <td className="p-3 align-middle">
                                  {cottage.cottage_date}
                                </td>

                                <td className="p-3 align-middle">
                                  {cottage.proforma?.prf_number || "—"}
                                </td>

                                <td className="p-3 align-middle">
                                  {cottage.is_patteh ? (
                                    "پته"
                                  ) : cottage.proforma?.prf_order_no ? (
                                    <Link
                                      className="text-blue-700 hover:underline"
                                      to={`/order-details/${cottage.proforma.prfVCodeInt}`}
                                    >
                                      {cottage.proforma.prf_order_no}
                                    </Link>
                                  ) : (
                                    "—"
                                  )}
                                </td>

                                <td className="p-3 align-middle">
                                  {customer ? (
                                    <Link
                                      className="text-blue-700 hover:underline"
                                      to={`/customers/details/${customer.id}`}
                                    >
                                      {customer.full_name}
                                    </Link>
                                  ) : (
                                    "—"
                                  )}
                                </td>

                                <td className="p-3 align-middle">
                                  {formatNumber(cottage.total_value)}
                                </td>

                                <td className="p-3 align-middle">
                                  {formatNumber(cottage.customs_value)}
                                </td>

                                <td className="p-3 align-middle">
                                  {formatNumber(cottage.total_expenses)}
                                </td>

                                <td className="p-3 align-middle">
                                  {updatingCurrencyPrice &&
                                  updatingCurrencyPrice[cottage.id] ? (
                                    <span className="inline-flex items-center gap-1 text-blue-700">
                                      <Loader2 className="w-4 h-4 animate-spin" />
                                      در حال به‌روزرسانی…
                                    </span>
                                  ) : updateCurrencyPriceError &&
                                    updateCurrencyPriceError[cottage.id] ? (
                                    <span className="text-red-600">
                                      {String(
                                        updateCurrencyPriceError[cottage.id]
                                      )}
                                    </span>
                                  ) : cottage.currency_price ? (
                                    `${cottage.currency_price} ریال`
                                  ) : (
                                    "—"
                                  )}
                                </td>

                                <td className="p-3 align-middle">
                                  {formatNumber(cottage.added_value)}
                                </td>

                                <td className="p-3 align-middle">
                                  {cottage.rafee_taahod ? "بله" : "خیر"}
                                </td>

                                <td className="p-3 align-middle">
                                  {cottage.docs_recieved ? "بله" : "خیر"}
                                </td>

                                <td className="p-3 align-middle">
                                  {cottage.rewatch ? "بله" : "خیر"}
                                </td>

                                <td className="p-3 align-middle">
                                  {cottage.exited ? "بله" : "خیر"}
                                </td>

                                <td className="p-3 align-middle">
                                  <Link
                                    className="text-blue-700 hover:underline"
                                    to={`/cottages/${cottage.cottage_number}`}
                                  >
                                    جزئیات
                                  </Link>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <div className="mt-3">
                    <PaginationControls
                      currentPage={currentPage}
                      totalPages={totalPages}
                      pageSize={pageSize}
                      pageSizeOptions={[10, 20, 50, 100, 200]}
                      hasNext={hasNext}
                      hasPrevious={hasPrevious}
                      onPageChange={handlePageChange}
                      onNextPage={handleNextPage}
                      onPreviousPage={handlePreviousPage}
                      onPageSizeChange={handlePageSizeChange}
                    />
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>

      {/* Sticky bulk-actions bar */}
      {selectedCottages.length > 0 && (
        <div className="fixed left-0 right-0 bottom-4 z-20 px-4">
          <div className="mx-auto max-w-[900px] rounded-3xl bg-white shadow-lg ring-1 ring-slate-200">
            <div className="p-3">
              <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                <div className="text-sm text-slate-700">
                  <b>{selectedCottages.length}</b> مورد انتخاب شده
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => setShowModal(true)}
                    className="inline-flex items-center gap-2 rounded-2xl bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700 transition"
                  >
                    <Trash2 className="w-4 h-4" />
                    حذف
                  </button>

                  <button
                    onClick={handleExport}
                    className="inline-flex items-center gap-2 rounded-2xl bg-emerald-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-emerald-700 transition"
                  >
                    <FileSpreadsheet className="w-4 h-4" />
                    اکسل
                  </button>

                  {/* NEW: Export current filter (all records) */}
                  <button
                    onClick={handleExportFiltered}
                    className="inline-flex items-center gap-2 rounded-2xl bg-emerald-50 px-3 py-1.5 text-sm font-medium text-emerald-700 hover:bg-emerald-100 transition ring-1 ring-emerald-200"
                  >
                    <FileSpreadsheet className="w-4 h-4" />
                    اکسلِ فیلتر
                  </button>

                  {/* <button
                    onClick={handleStartAutomation}
                    disabled={automationLoading}
                    className="inline-flex items-center gap-2 rounded-2xl bg-sky-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-sky-700 transition disabled:bg-slate-200 disabled:text-slate-500 disabled:cursor-not-allowed"
                  >
                    {automationLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        در حال اجرا…
                      </>
                    ) : (
                      <>
                        <Play className="w-4 h-4" />
                        اتوماسیون EPL
                      </>
                    )}
                  </button> */}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Slide-Over Filters */}
      <FilterSlideOver
        isOpen={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        title="فیلترها"
        badgeCount={activeFiltersCount}
        onClear={clearFilters}
        onApply={() => setFiltersOpen(false)}
        dir="rtl"
      >
        <AccordionItem title="سفارش" defaultOpen>
          <div className="space-y-2">
            <label className="block text-sm font-medium text-slate-700 text-right">
              ثبت سفارش:
            </label>
            <Select
              value={
                orderOptions.find((option) => option.value === prfOrderNo) ||
                null
              }
              onChange={(opt) => setPrfOrderNo(opt ? opt.value : "")}
              options={orderOptions}
              isClearable
              placeholder="انتخاب سفارش"
              styles={selectStyles}
              className="text-sm"
            />
          </div>
        </AccordionItem>

        <AccordionItem title="تاریخ‌های اظهارنامه" defaultOpen>
          <div className="grid grid-cols-1 gap-3">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-slate-700 text-right">
                تاریخ شروع:
              </label>
              <DatePicker
                style={{
                  display: "flex",
                  alignItems: "center",
                  padding: "1.15rem",
                  width: "100%",
                  borderColor: "#E2E8F0",
                  borderRadius: "1rem",
                  height: "2.8rem",
                  boxShadow: "none",
                }}
                value={cottageDate}
                onChange={handleCottageDateChange}
                calendar={persian}
                locale={persian_fa}
                format="YYYY-MM-DD"
                numerals="en"
                placeholder="تاریخ شروع"
                className="w-full"
                clearable
              />
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-slate-700 text-right">
                تاریخ پایان:
              </label>
              <DatePicker
                style={{
                  display: "flex",
                  alignItems: "center",
                  padding: "1.15rem",
                  width: "100%",
                  borderColor: "#E2E8F0",
                  borderRadius: "1rem",
                  height: "2.8rem",
                  boxShadow: "none",
                }}
                value={cottageDateBefore}
                onChange={handleCottageDateBeforeChange}
                calendar={persian}
                locale={persian_fa}
                format="YYYY-MM-DD"
                numerals="en"
                placeholder="تاریخ پایان"
                className="w-full"
                clearable
              />
            </div>
          </div>
        </AccordionItem>

        <AccordionItem title="تاریخ‌های پرداخت و مرجع" defaultOpen>
          <div className="grid grid-cols-1 gap-3">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-slate-700 text-right">
                تاریخ پرداخت (از):
              </label>
              <DatePicker
                style={{
                  display: "flex",
                  alignItems: "center",
                  padding: "1.15rem",
                  width: "100%",
                  borderColor: "#E2E8F0",
                  borderRadius: "1rem",
                  height: "2.8rem",
                  boxShadow: "none",
                }}
                value={paymentDateAfter}
                onChange={handlePaymentDateAfterChange}
                calendar={persian}
                locale={persian_fa}
                format="YYYY-MM-DD"
                numerals="en"
                placeholder="YYYY-MM-DD"
                className="w-full"
                clearable
              />
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-slate-700 text-right">
                تاریخ پرداخت (تا):
              </label>
              <DatePicker
                style={{
                  display: "flex",
                  alignItems: "center",
                  padding: "1.15rem",
                  width: "100%",
                  borderColor: "#E2E8F0",
                  borderRadius: "1rem",
                  height: "2.8rem",
                  boxShadow: "none",
                }}
                value={paymentDateBefore}
                onChange={handlePaymentDateBeforeChange}
                calendar={persian}
                locale={persian_fa}
                format="YYYY-MM-DD"
                numerals="en"
                placeholder="YYYY-MM-DD"
                className="w-full"
                clearable
              />
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-slate-700 text-right">
                شماره مرجع:
              </label>
              <Select
                options={[
                  { value: "", label: "همه" },
                  { value: "True", label: "موجود" },
                  { value: "False", label: "ناموجود" },
                ]}
                value={
                  [
                    { value: "", label: "همه" },
                    { value: "True", label: "موجود" },
                    { value: "False", label: "ناموجود" },
                  ].find((opt) => opt.value === referencePresent) || null
                }
                onChange={(opt) => setReferencePresent(opt ? opt.value : "")}
                placeholder="انتخاب کنید"
                styles={selectStyles}
                className="text-sm"
                isClearable
              />
            </div>
          </div>
        </AccordionItem>

        <AccordionItem title="وضعیت‌ها" defaultOpen>
          <div className="space-y-3">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-slate-700 text-right">
                رفع تعهد:
              </label>
              <Select
                options={[
                  { value: "", label: "همه" },
                  { value: "True", label: "بله" },
                  { value: "False", label: "خیر" },
                ]}
                value={
                  [
                    { value: "", label: "همه" },
                    { value: "True", label: "بله" },
                    { value: "False", label: "خیر" },
                  ].find((opt) => opt.value === rafeeTaahod) || null
                }
                onChange={(opt) => setRafeeTaahod(opt ? opt.value : "")}
                placeholder="همه"
                styles={selectStyles}
                className="text-sm"
                isClearable
              />
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-slate-700 text-right">
                اخذ مدارک:
              </label>
              <Select
                options={[
                  { value: "", label: "همه" },
                  { value: "True", label: "بله" },
                  { value: "False", label: "خیر" },
                ]}
                value={
                  [
                    { value: "", label: "همه" },
                    { value: "True", label: "بله" },
                    { value: "False", label: "خیر" },
                  ].find((opt) => opt.value === docsRecieved) || null
                }
                onChange={(opt) => setDocsRecieved(opt ? opt.value : "")}
                placeholder="همه"
                styles={selectStyles}
                className="text-sm"
                isClearable
              />
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-slate-700 text-right">
                بازبینی:
              </label>
              <Select
                options={[
                  { value: "", label: "همه" },
                  { value: "True", label: "بله" },
                  { value: "False", label: "خیر" },
                ]}
                value={
                  [
                    { value: "", label: "همه" },
                    { value: "True", label: "بله" },
                    { value: "False", label: "خیر" },
                  ].find((opt) => opt.value === rewatchStatus) || null
                }
                onChange={(opt) => setRewatchStatus(opt ? opt.value : "")}
                placeholder="همه"
                styles={selectStyles}
                className="text-sm"
                isClearable
              />
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-slate-700 text-right">
                خروج شده:
              </label>
              <Select
                options={[
                  { value: "", label: "همه" },
                  { value: "True", label: "بله" },
                  { value: "False", label: "خیر" },
                ]}
                value={
                  [
                    { value: "", label: "همه" },
                    { value: "True", label: "بله" },
                    { value: "False", label: "خیر" },
                  ].find((opt) => opt.value === exitedStatus) || null
                }
                onChange={(opt) => setExitedStatus(opt ? opt.value : "")}
                placeholder="همه"
                styles={selectStyles}
                className="text-sm"
                isClearable
              />
            </div>
          </div>
        </AccordionItem>
      </FilterSlideOver>
    </div>
  );
};

export default CottageList;
